import React, { Component } from "react";
import { Label, Button, Icon, Modal, Form, TextArea, Message } from "semantic-ui-react";

import FileInput from "../../components/FileInput/FileInput";
import { statusLabel, isTeacherEditable } from "../../utils/workflowStatus";
import {
  LADDER,
  STATUS_COLOR,
  awaitsUser,
  waitingLabel,
} from "../../utils/clubWorkflow";

/**
 * Where a club's marks stand on the approval ladder, and the one move the
 * person looking at it may make.
 *
 * The subject version of this is a 1305-line component carrying file uploads,
 * PACE confirmations and per-step note history. None of that applies to a club,
 * so this is its own, much smaller thing - but it climbs the identical ladder
 * and writes the identical fields, and it reads the status through the shared
 * helper, so the two cannot drift apart.
 *
 *   To Do -> Created -> Checked -> Reviewed -> Approved
 *             moderator  levelHead  registrar  principal
 *
 * Discard sends it back to the moderator with a reason, from any step.
 */

class ClubApproval extends Component {
  state = { discarding: false, notes: "" };

  /** Discarding is the approvers' way back; it is not the moderator's. */
  mayDiscard(status) {
    const { userType } = this.props;
    if (isTeacherEditable(status) || status === "Approved") return false;
    return (
      userType === "admin" ||
      userType === "levelHead" ||
      userType === "registrar" ||
      userType === "principal"
    );
  }

  submitDiscard = () => {
    const notes = this.state.notes.trim();
    if (!notes) return;

    this.props.onAdvance(this.props.club, "Discarded", notes);
    this.setState({ discarding: false, notes: "" });
  };

  /** Where the attached sheet lives, read from the row rather than rebuilt. */
  downloadLink(detail) {
    if (!detail || !detail.file) return null;
    return `${process.env.REACT_APP_URL}/containers/` +
      `${process.env.REACT_APP_CONTAINER}/download/` +
      encodeURIComponent(detail.file);
  }

  render() {
    const { club, detail, busy, term } = this.props;

    if (!detail) {
      return (
        <Message warning size="small">
          {club.name} has no workflow row for {term} yet, so its marks cannot be
          submitted. Ask your administrator to run the club seeding script.
        </Message>
      );
    }

    const status = detail.status || "To Do";
    const rung = LADDER[status];
    const mine = awaitsUser(status, {
      userType: this.props.userType,
      isModerator: this.props.isModerator,
    });

    return (
      <div style={{ marginBottom: "1em" }}>
        <Label color={STATUS_COLOR[status] || "grey"} size="large">
          {statusLabel(status)}
        </Label>

        {status === "Approved" ? (
          <span style={{ marginLeft: "1em" }}>
            <Icon name="check" color="green" />
            Signed off by the principal. Nothing further to do.
          </span>
        ) : mine ? (
          <Button
            primary
            size="small"
            disabled={busy}
            style={{ marginLeft: "1em" }}
            onClick={() => this.props.onAdvance(club, rung.next)}
          >
            {rung.action}
          </Button>
        ) : (
          <span style={{ marginLeft: "1em" }}>{`${waitingLabel(status)}.`}</span>
        )}

        {/* The sheet everyone downstream reads. Attached by the moderator when
            they submit, and readable by anyone on the ladder after that. */}
        {this.downloadLink(detail) && (
          <Button
            basic
            size="small"
            as="a"
            href={this.downloadLink(detail)}
            style={{ marginLeft: "0.5em" }}
          >
            <Icon name="file excel outline" />
            Grade sheet
          </Button>
        )}

        {this.mayDiscard(status) && (
          <Button
            basic
            size="small"
            disabled={busy}
            style={{ marginLeft: "0.5em" }}
            onClick={() => this.setState({ discarding: true })}
          >
            Send back
          </Button>
        )}

        {/* Attaching is the moderator's step, and only while it is their move. */}
        {mine && rung && rung.next === "Created" && (
          <div style={{ marginTop: "1em" }}>
            <strong>
              {detail.file
                ? "Attach a new grade sheet to replace the one on file:"
                : "Attach the grade sheet for this term:"}
            </strong>
            <FileInput handleFileChange={this.props.onClubSheetChange} />
            {this.props.sheetName && (
              <p>
                <Icon name="check" color="green" />
                {`Ready to submit: ${this.props.sheetName.split("/").pop()}`}
              </p>
            )}
          </div>
        )}

        <Modal
          open={this.state.discarding}
          size="small"
          closeIcon
          onClose={() => this.setState({ discarding: false, notes: "" })}
        >
          <Modal.Header>{`Send ${club.name} back to its moderator`}</Modal.Header>
          <Modal.Content>
            <Form>
              <Form.Field
                control={TextArea}
                label="What needs fixing?"
                placeholder="The moderator sees this, so say what to change."
                value={this.state.notes}
                onChange={(e, d) => this.setState({ notes: d.value })}
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button
              color="red"
              disabled={!this.state.notes.trim()}
              onClick={this.submitDiscard}
            >
              <Icon name="undo" /> Send back
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
    );
  }
}

export default ClubApproval;
